"use client"

import { Book, Users, Star } from "lucide-react"
import Image from "next/image"
import Link from "@/components/Link"
import { Card } from "@/components/ui/card"
import type { ILecturer } from "@/lib/api/lecturers"
import { getLecturerPath } from "@/lib/helpers/imageHelpers"

interface ExpertsClientProps {
  experts: ILecturer[]
}

export default function ExpertsClient({ experts }: ExpertsClientProps) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {experts.map((expert) => (
        <Link key={expert.slug} href={`/lecturer/${expert.slug}`}>
          <Card className="group h-full overflow-hidden transition-all hover:shadow-lg">
            <div className="relative h-48 w-full overflow-hidden">
              {expert.cover_image ? (
                <Image
                  src={getLecturerPath(expert)}
                  alt={`${expert.name} Cover`}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-r from-teal-500 to-blue-500" />
              )}
              <div className="-bottom-0 absolute left-4 z-0">
                <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-white shadow-lg">
                  <Image
                    src={getLecturerPath(expert)}
                    alt={expert.name}
                    width={80}
                    height={80}
                    className="rounded-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 pt-12">
              <h2 className="font-semibold text-gray-900 text-xl">{expert.name}</h2>
              <p className="mt-1 font-bold text-gray-600 text-sm">{expert.level}</p>
              <p className="mt-1 text-gray-600 text-sm">{expert.descriptions}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {expert.expertise?.split(",").slice(0, 3).map((skill) => (
                  <span
                    key={skill.trim()}
                    className="rounded-full bg-teal-50 px-3 py-1 text-teal-600 text-xs"
                  >
                    {skill.trim()}
                  </span>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-3 gap-2 border-gray-100 border-t pt-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="font-semibold text-gray-900">
                      {expert.students_total}
                    </span>
                  </div>
                  <p className="mt-1 text-gray-500 text-xs">Học viên</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Book className="h-4 w-4 text-gray-400" />
                    <span className="font-semibold text-gray-900">
                      {expert.course_count}
                    </span>
                  </div>
                  <p className="mt-1 text-gray-500 text-xs">Khóa học</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Star className="h-4 w-4 text-gray-400" />
                    <span className="font-semibold text-gray-900">
                      {expert.average_rating}
                    </span>
                  </div>
                  <p className="mt-1 text-gray-500 text-xs">Đánh giá</p>
                </div>
              </div>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  )
} 